import { Component, Input, OnChanges, LOCALE_ID, Inject  } from '@angular/core';
import { formatNumber } from '@angular/common';
import { Icons } from '@shared/services/svgIcons.service';

@Component({
  selector: 'object3d-performance',
  templateUrl: './object3d-performance.component.html',
  styleUrls: ['./object3d-performance.component.scss']
})
export class Object3DPerformanceComponent implements OnChanges {
  @Input() stats: any;
  @Input() isAnnotation: boolean;
  public performanceReport: Array<PerformanceLineReport>;
  constructor(@Inject(LOCALE_ID) private locale: string) { }

  ngOnChanges(): void {
    this.performanceReport = computePerformanceReport(this.stats, this.isAnnotation, this.locale);
  }

  getStatusIcon(status: StatusType): string {
      switch (status) {
        case StatusType.OK:
          return Icons.PerformanceOk;
        case StatusType.WARNING:
          return Icons.PerformanceWarning;
        case StatusType.ERROR:
          return Icons.PerformanceError;
      }
  }
}

function computePerformanceReport(stats: any, isAnnotation: boolean, locale: string): Array<PerformanceLineReport> {
  const oPerformanceLineReport = [];
  let errorComparisonValue;
  let recommendedComparisonValue;
  if (isAnnotation) {
    errorComparisonValue = new ComparisonValue(10, 10000, 6, 4096, 60 );
    recommendedComparisonValue = new ComparisonValue( 3, 5000, 3, 1024, 15 );
  } else {
    errorComparisonValue = new ComparisonValue(50, 75000, 25, 4096, 512 );
    recommendedComparisonValue = new ComparisonValue( 16, 50000, 10, 2048, 128 );
  }
  {
    const fileSize = Math.ceil(stats.fileSize / (1024 * 1024 ) * 100) / 100;
    const { status, tooltip } = computeStatusAndTooltips(fileSize, recommendedComparisonValue.fileSize, errorComparisonValue.fileSize, 'Mo', locale);
    const fileSizeLineReport = new PerformanceLineReport(status, 'File size', formatNumber(fileSize, locale), 'Mo', tooltip );
    oPerformanceLineReport.push(fileSizeLineReport);
  }
  {
    const { status, tooltip } = computeStatusAndTooltips(stats.meshes.triangles, recommendedComparisonValue.triangleCount, errorComparisonValue.triangleCount,  '', locale);
    const triangleReport = new PerformanceLineReport(status, 'Triangles', formatNumber(stats.meshes.triangles, locale), '', tooltip );
    oPerformanceLineReport.push(triangleReport);
  }
  {
    const maxResolution = Math.max(stats.textures.maxWidthResolution, stats.textures.maxHeightResolution);
    const { status, tooltip } = computeStatusAndTooltips(maxResolution, recommendedComparisonValue.maxResolution, errorComparisonValue.maxResolution,  'px', locale);
    const triangleReport = new PerformanceLineReport(status, 'Max texture size', formatNumber(maxResolution, locale), 'px', tooltip );
    oPerformanceLineReport.push(triangleReport);
  }
  {
    const gpuSize = Math.ceil(stats.textures.gpuMinimumTotalSize / (1000 * 1000 ) * 100) / 100;
    if (!isNaN(gpuSize)) {
      const { status, tooltip } = computeStatusAndTooltips(gpuSize, recommendedComparisonValue.gpuMinimumTotalSize, errorComparisonValue.gpuMinimumTotalSize, 'Mo', locale);
      const triangleReport = new PerformanceLineReport(status, 'Texture gpu size', formatNumber(gpuSize, locale), 'Mo', tooltip );
      oPerformanceLineReport.push(triangleReport);
    }
  }
  {
    const { status, tooltip } = computeStatusAndTooltips(stats.materials.count, recommendedComparisonValue.materialCount, errorComparisonValue.materialCount,  '', locale);
    const triangleReport = new PerformanceLineReport(status, 'Materials', stats.materials.count, '', tooltip );
    oPerformanceLineReport.push(triangleReport);
  }

  return oPerformanceLineReport;
}

function computeStatusAndTooltips(value: number, recommendedValue: number, errorValue: number, unit: string, locale: string): { status: StatusType, tooltip: string } {
  if (value <= recommendedValue) {
    return { status: StatusType.OK, tooltip: '' };
  }
  if (value > errorValue) {
    return { status: StatusType.ERROR, tooltip: `Maximum value is ${formatNumber(errorValue, locale)}${unit}`};
  }

  return { status: StatusType.WARNING, tooltip: `Recommended value is ${formatNumber(recommendedValue, locale)}${unit} or below`};
}

class PerformanceLineReport {
  constructor(public statusType: StatusType, public name: string, public value: string, public unit: string, public tooltip: string ) {}
}

class ComparisonValue {
  constructor(public fileSize: number, public triangleCount: number, public materialCount: number, public maxResolution: number, public gpuMinimumTotalSize: number ) {}
}

enum StatusType {
  OK,
  WARNING,
  ERROR
}
